import { useEffect, useRef } from 'react';

import {
  getCategoryEntry,
  toCategoryFlagState,
} from '@/utils/booking/categoryTrackingProps';
import { Tracker } from '@/utils/tracking';

import type {
  UseServiceStepTrackingParams,
  UseServiceStepTrackingResult,
} from './types';

/**
 * Events of the category-filtered services step. They fire only with
 * category-first on and an L2 `categoryId`, or on the grouped flat list, where
 * the ids come from the service's first category.
 */
export function useServiceStepTracking({
  shop,
  barberIdOrRoute,
  groupId,
  categoryId,
  isCategoryFirstEnabled,
  isFlatList,
  services,
  categoryRows,
  selectedServices,
  allowMultipleServices,
}: UseServiceStepTrackingParams): UseServiceStepTrackingResult {
  const isFiltered = isCategoryFirstEnabled && !!categoryId;
  const isFlat = isCategoryFirstEnabled && isFlatList;
  const entry = getCategoryEntry(
    barberIdOrRoute === 'any' ? undefined : barberIdOrRoute,
  );
  const flagState = toCategoryFlagState(isCategoryFirstEnabled);
  const commonProps = {
    shop_id: shop.id,
    brand_id: shop.brandId,
    entry,
    flag_state: flagState,
  };

  // The view fires once per mount, even if flat mode toggles.
  const hasTrackedFlatViewRef = useRef(false);
  useEffect(() => {
    if (!isFlat || hasTrackedFlatViewRef.current) return;
    hasTrackedFlatViewRef.current = true;
    Tracker.track('booking_flat_list_viewed', {
      shop_id: shop.id,
      brand_id: shop.brandId,
      entry,
      flag_state: flagState,
    });
  }, [isFlat, shop.id, shop.brandId, entry, flagState]);

  const getCategoryIds = (serviceId: string) => {
    if (!isFlat) return { l1_id: groupId, l2_id: categoryId };
    const category = services.find(({ id }) => id === serviceId)
      ?.categories?.[0];
    if (!category) return null;
    const row = categoryRows?.find(({ id }) => id === category.id);
    const parentId = row ? row.parentCategoryId : category.parentCategoryId;
    // A category without a parent is itself the L1 group.
    return parentId
      ? { l1_id: parentId, l2_id: category.id }
      : { l1_id: category.id };
  };

  const trackServiceAdded = (serviceId: string) => {
    const categoryIds = getCategoryIds(serviceId);
    if (!(isFiltered || isFlat) || !categoryIds) return;
    const props = { ...commonProps, ...categoryIds, service_id: serviceId };
    Tracker.track('booking_service_selected_from_category', props);
    Tracker.track('booking_cart_service_added', {
      ...props,
      // Without allowMultipleServices, changeService replaces the selection.
      cart_size: allowMultipleServices ? selectedServices.length + 1 : 1,
    });
  };

  const trackContinue = () => {
    if (!(isFiltered || isFlat)) return;
    Tracker.track('booking_style_filter_applied', {
      ...commonProps,
      ...(!isFlat && { l2_id: categoryId }),
      // TrackingContext accepts only scalar values, so the ids are joined.
      service_ids: selectedServices.map(({ id }) => id).join(','),
    });
  };

  return { trackServiceAdded, trackContinue };
}
